import { useState, useCallback } from 'react';
import { Pencil, Copy, Send, Check, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { markdownComponents, remarkPlugins } from '../utils/markdownConfig';
import type { WidgetStatesMap } from '../types/widgetState';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.autumnplatform.com').replace(/\/$/, '');

/**
 * Support Ticket Compose Widget — V1
 *
 * Email-style composer rendered when the chatbot wants to escalate a question
 * to Autumn's support team. Mirrors the screenshot the team agreed on:
 * Subject + Email Body + Edit / Copy / Send / Dismiss actions.
 *
 * Replaces the legacy `suggestion-action` / `current` / `suggested` table for
 * the support-ticket flow specifically. Pricing widgets continue to use the
 * older table-style component.
 */

export type SupportTicketComposeWidgetSpec = {
  widgetId: string;
  type: 'support-ticket-compose';
  subject: string;
  emailBody: string;
  recipientEmail: string;
  apiCall: {
    endpoint: string;
    method: 'POST';
    payload: {
      subject: string;
      message: string;
      email: string;
      conversationContext: Array<{ role: string; content: string }>;
      source: 'chatbot';
    };
  };
  userActions?: Array<'send' | 'edit' | 'copy' | 'dismiss'>;
};

type WidgetState = 'idle' | 'editing' | 'sending' | 'success' | 'error' | 'dismissed';

/**
 * When the team will reply, as judged by the backend at submission
 * (`replyExpectation` on the `POST /support-tickets` response, AUT-301):
 * `next_business_day` from Friday 7pm to Sunday 10pm ET, else
 * `within_24_hours`. Persisted in the widget state's `appliedPayload` so a
 * reload repeats what the customer was told when they sent it.
 */
export type SupportReplyExpectation = 'within_24_hours' | 'next_business_day';

function parseReplyExpectation(value: unknown): SupportReplyExpectation {
  return value === 'next_business_day' ? 'next_business_day' : 'within_24_hours';
}

interface SupportTicketComposeWidgetProps {
  widget: SupportTicketComposeWidgetSpec;
  onDismiss?: () => void;
  onSendSuccess?: () => void;
  conversationId?: string;
  messageId?: string;
  partIndex?: number;
  initialState?: 'idle' | 'success' | 'dismissed';
  initialPersistedStatus?: 'applied' | 'dismissed';
  widgetStates?: WidgetStatesMap;
  onWidgetActive?: (
    key: string,
    info: {
      conversationId: string;
      messageId: string;
      actionType: string;
      widgetId?: string;
      partIndex?: number;
      useNewPersistence?: boolean;
    },
  ) => void;
  onWidgetInactive?: (key: string) => void;
  useNewPersistence?: boolean;
}

export function SupportTicketComposeWidget({
  widget,
  onSendSuccess,
  conversationId,
  messageId,
  partIndex,
  initialState = 'idle',
  initialPersistedStatus,
  widgetStates,
  onWidgetActive,
  onWidgetInactive,
  useNewPersistence = false,
}: SupportTicketComposeWidgetProps) {
  const [widgetState, setWidgetState] = useState<WidgetState>(initialState);
  // Set by this session's Send; otherwise read from the persisted state, which
  // can arrive after mount, so it is derived per render rather than seeded.
  const [sentReplyExpectation, setSentReplyExpectation] =
    useState<SupportReplyExpectation | null>(null);
  const replyExpectation =
    sentReplyExpectation ??
    parseReplyExpectation(
      widget.widgetId ? widgetStates?.[widget.widgetId]?.appliedPayload?.replyExpectation : undefined,
    );
  const [editedSubject, setEditedSubject] = useState(widget.subject);
  const [editedBody, setEditedBody] = useState(widget.emailBody);
  const [errorMessage, setErrorMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const actions = widget.userActions ?? ['send', 'edit', 'copy', 'dismiss'];
  const registrationKey = widget.widgetId
    ?? (messageId && partIndex != null ? `${messageId}:${partIndex}` : null);
  const canPersist = !!widget.widgetId || (!!messageId && partIndex != null);

  // INTENTIONALLY no onWidgetActive registration.
  //
  // Pricing suggestions register so the parent's dismissActiveWidgets() can
  // mass-dismiss any unresolved suggestions when the user navigates away —
  // a stale rate suggestion shouldn't persist into the next session.
  //
  // Support tickets are different: they're drafts the customer may want to
  // come back to. By NOT registering, we guarantee the widget never appears
  // in activeWidgetsRef, which means dismissActiveWidgets cannot persist it
  // as `dismissed` no matter where it fires from (parent unmount, conversation
  // switch, or "New Chat"). The widget still calls persistWidgetState('applied')
  // directly from handleSend on success, so the success-card-on-reload path
  // continues to work end-to-end.
  //
  // Reference parameters so eslint doesn't flag them as unused:
  void onWidgetActive;
  void onWidgetInactive;
  void registrationKey;

  const persistWidgetState = useCallback(async (
    status: 'applied' | 'dismissed',
    appliedPayload?: Record<string, unknown>,
  ) => {
    if (!conversationId || !canPersist) return;
    try {
      const { api } = await import('../../helpers/api');
      if (useNewPersistence && widget.widgetId) {
        await api.conversations.updateWidgetState(conversationId, widget.widgetId, {
          messageId: messageId ?? '',
          actionType: 'open-support-ticket',
          status,
          ...(appliedPayload && { appliedPayload }),
        });
      } else if (messageId) {
        await api.pricingConfigAgent.updateWidgetStatus(
          conversationId,
          messageId,
          partIndex ?? 0,
          status,
          widget.widgetId,
        );
      }
    } catch (err) {
      console.error('[SupportTicketComposeWidget] persist failed:', err);
    }
  }, [useNewPersistence, widget.widgetId, conversationId, messageId, partIndex, canPersist]);

  const handleSend = async () => {
    if (!editedSubject.trim() || !editedBody.trim()) {
      setErrorMessage('Subject and message are required.');
      return;
    }

    setWidgetState('sending');
    setErrorMessage('');
    // Nothing to unregister — we intentionally never registered (see above).

    try {
      const authToken = localStorage.getItem('token');
      const fullEndpoint = widget.apiCall.endpoint.startsWith('http')
        ? widget.apiCall.endpoint
        : `${API_BASE_URL}${widget.apiCall.endpoint}`;

      // Merge any edits into the payload the backend originally streamed —
      // subject/message/email/source/conversationContext flow through.
      const payload = {
        ...widget.apiCall.payload,
        subject: editedSubject.trim(),
        message: editedBody.trim(),
      };

      const response = await fetch(fullEndpoint, {
        method: widget.apiCall.method,
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        let body: { replyExpectation?: unknown } | null = null;
        try {
          body = await response.json();
        } catch {
          // body wasn't JSON — fall back to the 24-hour promise
        }
        const expectation = parseReplyExpectation(body?.replyExpectation);
        setSentReplyExpectation(expectation);
        setWidgetState('success');
        onSendSuccess?.();
        persistWidgetState('applied', { replyExpectation: expectation });
        return;
      }

      let errorMsg = 'Failed to send the support ticket. Please try again.';
      try {
        const error = await response.json();
        errorMsg = error?.message ?? errorMsg;
      } catch {
        // body wasn't JSON — keep the generic message
      }
      setWidgetState('error');
      setErrorMessage(errorMsg);
    } catch (err) {
      setWidgetState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Network error. Please try again.');
    }
  };

  const handleCopy = async () => {
    const text = `Subject: ${editedSubject}\n\n${editedBody}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1500);
    } catch (err) {
      console.warn('[SupportTicketComposeWidget] clipboard write failed:', err);
    }
  };

  const enterEditMode = () => {
    if (widgetState === 'idle' || widgetState === 'error') {
      setWidgetState('editing');
    }
  };

  const exitEditMode = () => {
    if (widgetState === 'editing') {
      setWidgetState('idle');
    }
  };

  // Persisted "already sent" state from a prior session — just show success.
  if (initialPersistedStatus === 'applied' && widgetState === 'idle') {
    return (
      <SupportTicketSuccessCard
        subject={editedSubject}
        recipientEmail={widget.recipientEmail}
        replyExpectation={replyExpectation}
      />
    );
  }

  if (initialPersistedStatus === 'dismissed' || widgetState === 'dismissed') {
    return null;
  }

  if (widgetState === 'success') {
    return (
      <SupportTicketSuccessCard
        subject={editedSubject}
        recipientEmail={widget.recipientEmail}
        replyExpectation={replyExpectation}
      />
    );
  }

  const isEditing = widgetState === 'editing';
  const isSending = widgetState === 'sending';
  const sendDisabled = isSending || !editedSubject.trim() || !editedBody.trim();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.15 }}
        className="bg-surface-primary border border-border-primary rounded-lg shadow-sm overflow-hidden"
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border-secondary">
          <div className="flex items-center gap-2">
            {actions.includes('edit') && (
              <button
                type="button"
                onClick={isEditing ? exitEditMode : enterEditMode}
                disabled={isSending}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  isEditing
                    ? 'bg-brand-primary/10 text-brand-primary'
                    : 'text-content-secondary hover:bg-surface-secondary'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                aria-label={isEditing ? 'Exit edit mode' : 'Edit ticket'}
                title={isEditing ? 'Done editing' : 'Edit'}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span>{isEditing ? 'Done' : 'Edit'}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-1">
            {actions.includes('copy') && (
              <button
                type="button"
                onClick={handleCopy}
                disabled={isSending}
                className="p-1.5 rounded-md text-content-secondary hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={copyStatus === 'copied' ? 'Copied' : 'Copy ticket text'}
                title={copyStatus === 'copied' ? 'Copied!' : 'Copy'}
              >
                {copyStatus === 'copied'
                  ? <Check className="h-4 w-4 text-brand-primary" />
                  : <Copy className="h-4 w-4" />}
              </button>
            )}
            {actions.includes('send') && (
              <button
                type="button"
                onClick={handleSend}
                disabled={sendDisabled}
                className="d-btn d-btn-primary d-btn-sm ml-1"
                aria-label="Send support ticket"
                title="Send"
              >
                <Send className="h-3.5 w-3.5" />
                <span>{isSending ? 'Sending…' : 'Send'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Subject row */}
        <div className="px-4 py-3 border-b border-border-secondary">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold text-content-secondary tracking-wide uppercase">
              Subject
            </span>
            {isEditing ? (
              <input
                type="text"
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                disabled={isSending}
                maxLength={140}
                className="flex-1 text-sm font-medium text-content-primary bg-transparent border-0 border-b border-border-secondary focus:border-brand-primary focus:outline-none px-0 py-0.5"
                placeholder="Subject"
                aria-label="Edit subject"
              />
            ) : (
              <span className="text-sm font-medium text-content-primary break-words">
                {editedSubject || (
                  <em className="text-content-tertiary">No subject</em>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Email body */}
        <div className="px-4 py-3">
          {isEditing ? (
            <textarea
              value={editedBody}
              onChange={(e) => setEditedBody(e.target.value)}
              disabled={isSending}
              rows={8}
              className="w-full text-sm text-content-primary bg-transparent border border-border-secondary rounded-md focus:border-brand-primary focus:outline-none px-3 py-2 resize-y leading-relaxed"
              placeholder="Write your message…"
              aria-label="Edit email body"
            />
          ) : (
            <div className="text-sm text-content-primary leading-relaxed [&>p]:my-1.5 [&>ul]:my-1.5 [&>ol]:my-1.5 [&_a]:text-brand-primary [&_a]:underline">
              <ReactMarkdown
                components={markdownComponents}
                remarkPlugins={remarkPlugins}
              >
                {editedBody}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Error banner */}
        {widgetState === 'error' && errorMessage && (
          <div className="px-4 py-2 border-t border-border-secondary bg-pink-primary/5 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-pink-primary mt-0.5 flex-shrink-0" />
            <span className="text-xs text-pink-primary">{errorMessage}</span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Compact "ticket sent" confirmation. Shown after a successful send AND
 * on conversation reload when the widget was previously sent.
 */
function SupportTicketSuccessCard({
  subject,
  recipientEmail,
  replyExpectation,
}: {
  subject: string;
  recipientEmail: string;
  replyExpectation: SupportReplyExpectation;
}) {
  const email = (
    <span className="font-medium text-content-secondary">
      {recipientEmail}
    </span>
  );
  return (
    <div className="bg-surface-primary border border-border-primary rounded-lg shadow-sm px-4 py-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="h-5 w-5 text-brand-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-content-primary">
          Support ticket sent
        </p>
        <p className="text-xs text-content-secondary mt-0.5 truncate">
          <span className="text-content-tertiary">Re:</span> {subject || 'your request'}
        </p>
        <p className="text-[11px] text-content-tertiary mt-1">
          {replyExpectation === 'next_business_day' ? (
            <>
              Thanks for reaching out. Your message has made it safely to Autumn. Our Hotel
              Partner Team is taking a little downtime, so we’ll email you at {email} on the
              next business day.
            </>
          ) : (
            <>
              The Autumn team will email you at {email} within 24 hours.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
